import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase/cliente'
import type { ActaCorteObra, RegistroCorteAvance, EstadoActaCorte, CapituloActaCorte, ItemActaCorte } from './tipos'
import type { ProyectoPlan } from '../tipos'

const STORAGE_ACTAS = 'mega_actas_corte_v1'
const STORAGE_CORTES = 'mega_cortes_avance_v1'
const sb: any = supabase

function cargarActasLocal(): ActaCorteObra[] {
  try {
    const raw = localStorage.getItem(STORAGE_ACTAS)
    if (raw) return JSON.parse(raw)
  } catch {}
  return []
}

function guardarActasLocal(actas: ActaCorteObra[]) {
  try {
    localStorage.setItem(STORAGE_ACTAS, JSON.stringify(actas))
    window.dispatchEvent(new CustomEvent('mega:actas-corte-actualizado'))
  } catch {}
}

function cargarCortesLocal(): RegistroCorteAvance[] {
  try {
    const raw = localStorage.getItem(STORAGE_CORTES)
    if (raw) return JSON.parse(raw)
  } catch {}
  return []
}

function guardarCortesLocal(cortes: RegistroCorteAvance[]) {
  try {
    localStorage.setItem(STORAGE_CORTES, JSON.stringify(cortes))
    window.dispatchEvent(new CustomEvent('mega:cortes-avance-actualizado'))
  } catch {}
}

export function generarSiguienteConsecutivoActa(existentes: ActaCorteObra[]): string {
  const anio = new Date().getFullYear()
  const prefijo = `ACT-${anio}-`
  let maxNum = 0
  existentes.forEach((a) => {
    if (a.consecutivo && a.consecutivo.startsWith(prefijo)) {
      const num = parseInt(a.consecutivo.replace(prefijo, ''), 10)
      if (!isNaN(num) && num > maxNum) maxNum = num
    }
  })
  return `${prefijo}${String(maxNum + 1).padStart(3, '0')}`
}

export function useActasCorte(proyectoId?: string) {
  const [actas, setActas] = useState<ActaCorteObra[]>(() => cargarActasLocal())
  const [cortes, setCortes] = useState<RegistroCorteAvance[]>(() => cargarCortesLocal())
  const [cargando, setCargando] = useState(false)

  const sincronizar = useCallback(async () => {
    try {
      setCargando(true)
      let query = sb.from('plan_actas_corte').select('*').eq('activo', true)
      if (proyectoId) {
        query = query.eq('proyecto_id', proyectoId)
      }
      const { data, error } = await query.order('fecha_corte', { ascending: false })

      if (!error && data) {
        const mapeadas: ActaCorteObra[] = data.map((row: any) => ({
          id: row.id,
          consecutivo: row.consecutivo,
          proyecto_id: row.proyecto_id,
          numero_corte: row.numero_corte,
          fecha_corte: row.fecha_corte,
          fecha_inicio_periodo: row.fecha_inicio_periodo,
          fecha_fin_periodo: row.fecha_fin_periodo,
          residente_nombre: row.residente_nombre,
          interventor_nombre: row.interventor_nombre,
          cliente_nombre: row.cliente_nombre || 'CLIENTE GENERAL',
          estado: row.estado as EstadoActaCorte,
          valor_costo_directo: Number(row.valor_costo_directo || 0),
          valor_aiu: Number(row.valor_aiu || 0),
          valor_total: Number(row.valor_total || 0),
          fecha_radicacion: row.fecha_radicacion,
          fecha_cobro: row.fecha_cobro,
          dias_credito: Number(row.dias_credito || 30),
          fecha_vencimiento: row.fecha_vencimiento,
          detalles_capitulos: Array.isArray(row.detalles_capitulos) ? row.detalles_capitulos : [],
          notas: row.notas,
          activo: Boolean(row.activo),
          created_at: row.created_at,
          updated_at: row.updated_at,
        }))
        setActas(mapeadas)
        guardarActasLocal(mapeadas)
      }
    } catch {
      // Uso de cache local
    } finally {
      setCargando(false)
    }
  }, [proyectoId])

  useEffect(() => {
    void sincronizar()

    const onActualizado = () => {
      setActas(cargarActasLocal())
      setCortes(cargarCortesLocal())
    }
    window.addEventListener('mega:actas-corte-actualizado', onActualizado)
    window.addEventListener('mega:cortes-avance-actualizado', onActualizado)
    return () => {
      window.removeEventListener('mega:actas-corte-actualizado', onActualizado)
      window.removeEventListener('mega:cortes-avance-actualizado', onActualizado)
    }
  }, [sincronizar])

  const actasProyecto = proyectoId ? actas.filter((a) => a.proyecto_id === proyectoId) : actas

  // 1. Registrar un corte físico con evidencia (Tarea 2.1)
  const registrarCorteAvance = useCallback(
    async (datos: {
      moduloId: string
      proyectoId: string
      etapaId: string
      cantidadContratada: number
      cantidadAnterior: number
      cantidadCorte: number
      quienReporto: string
      fotoEvidenciaUrl?: string | null
      observaciones?: string
    }): Promise<RegistroCorteAvance> => {
      const cantidadAcumulada = Number((datos.cantidadAnterior + datos.cantidadCorte).toFixed(2))
      const contratada = Math.max(0.01, datos.cantidadContratada)
      const avancePrevio = Math.min(100, Math.round((datos.cantidadAnterior / contratada) * 100))
      const avanceNuevo = Math.min(100, Math.round((cantidadAcumulada / contratada) * 100))

      const nuevoCorte: RegistroCorteAvance = {
        id: crypto.randomUUID(),
        modulo_id: datos.moduloId,
        proyecto_id: datos.proyectoId,
        etapa_id: datos.etapaId,
        cantidad_anterior: datos.cantidadAnterior,
        cantidad_corte: datos.cantidadCorte,
        cantidad_acumulada: cantidadAcumulada,
        cantidad_contratada: contratada,
        avance_previo: avancePrevio,
        avance_nuevo: avanceNuevo,
        quien_reporto: datos.quienReporto,
        fecha_corte: new Date().toISOString(),
        foto_evidencia_url: datos.fotoEvidenciaUrl || null,
        observaciones: datos.observaciones || '',
      }

      const listaActualizada = [nuevoCorte, ...cortes]
      setCortes(listaActualizada)
      guardarCortesLocal(listaActualizada)

      // Guardar en Supabase
      try {
        await sb.from('plan_avances_historial').insert({
          id: nuevoCorte.id,
          modulo_id: nuevoCorte.modulo_id,
          proyecto_id: nuevoCorte.proyecto_id,
          avance_previo: nuevoCorte.avance_previo,
          avance_nuevo: nuevoCorte.avance_nuevo,
          cantidad_corte: nuevoCorte.cantidad_corte,
          cantidad_acumulada: nuevoCorte.cantidad_acumulada,
          foto_evidencia_url: nuevoCorte.foto_evidencia_url,
          quien_reporto: nuevoCorte.quien_reporto,
          observaciones: nuevoCorte.observaciones,
          created_at: nuevoCorte.fecha_corte,
        })
      } catch {}

      return nuevoCorte
    },
    [cortes]
  )

  // 2. Generar Acta de Corte por Capítulo en PDF (Tarea 2.2)
  const generarActaCorte = useCallback(
    async (
      proyecto: ProyectoPlan,
      opciones: {
        residenteNombre: string
        interventorNombre?: string
        fechaCorte?: string
        fechaInicioPeriodo?: string
        fechaFinPeriodo?: string
        notas?: string
      }
    ): Promise<ActaCorteObra> => {
      const actasPrevias = actas.filter((a) => a.proyecto_id === proyecto.id)
      const numeroCorte = actasPrevias.length + 1
      const consecutivo = generarSiguienteConsecutivoActa(actas)
      const fechaCorte = opciones.fechaCorte || new Date().toISOString().split('T')[0]
      const ahora = new Date().toISOString()

      // Mapear cada capítulo y actividad con su cantidad contratada, anterior y este corte
      let costoDirectoCorteTotal = 0

      const capitulosActa: CapituloActaCorte[] = (proyecto.etapas || []).map((etapa, idx) => {
        let subtotalCap = 0

        const items: ItemActaCorte[] = (etapa.modulos || []).map((mod) => {
          const contratada = Number(mod.cantidad_contratada || mod.cantidad || 0)
          const acumulada = Number(mod.cantidad_ejecutada || 0)

          // Buscar cantidad del último corte previo para este módulo
          let cantidadAnterior = 0
          if (actasPrevias.length > 0) {
            const ultimaActa = actasPrevias[0]
            for (const cap of ultimaActa.detalles_capitulos || []) {
              const itemPrev = cap.items.find((it) => it.modulo_id === mod.id)
              if (itemPrev) {
                cantidadAnterior = itemPrev.cantidad_acumulada
                break
              }
            }
          }

          const cantidadEsteCorte = Math.max(0, Number((acumulada - cantidadAnterior).toFixed(2)))
          const saldo = Math.max(0, Number((contratada - acumulada).toFixed(2)))
          const pct = contratada > 0 ? Number(((acumulada / contratada) * 100).toFixed(1)) : 0
          const pUnit = Number(mod.precio_unitario || (contratada > 0 ? mod.valor_total / contratada : 0))
          const subtotalItem = Number((cantidadEsteCorte * pUnit).toFixed(2))

          subtotalCap += subtotalItem
          costoDirectoCorteTotal += subtotalItem

          return {
            modulo_id: mod.id,
            codigo: mod.codigo,
            nombre: mod.nombre,
            unidad: mod.unidad || 'UND',
            cantidad_contratada: contratada,
            cantidad_anterior: cantidadAnterior,
            cantidad_este_corte: cantidadEsteCorte,
            cantidad_acumulada: acumulada,
            saldo_por_ejecutar: saldo,
            porcentaje_ejecutado: pct,
            precio_unitario: pUnit,
            subtotal_corte: subtotalItem,
          }
        })

        return {
          etapa_id: etapa.id,
          orden: etapa.orden || idx + 1,
          nombre: etapa.nombre,
          items,
          subtotal_capitulo: Number(subtotalCap.toFixed(2)),
        }
      })

      // Cálculo de AIU del corte
      const aiu = proyecto.porcentaje_aiu_contrato || {
        admin: 10,
        imprevistos: 0,
        utilidad: 3,
        iva_utilidad: 19,
      }
      const adminPct = (aiu.admin || 0) / 100
      const imprevPct = (aiu.imprevistos || 0) / 100
      const utilPct = (aiu.utilidad || 0) / 100
      const ivaUtilPct = (aiu.iva_utilidad || 19) / 100

      const valAdmin = costoDirectoCorteTotal * adminPct
      const valImprev = costoDirectoCorteTotal * imprevPct
      const valUtil = costoDirectoCorteTotal * utilPct
      const valIvaUtil = valUtil * ivaUtilPct
      const valorAiuTotal = Number((valAdmin + valImprev + valUtil + valIvaUtil).toFixed(2))
      const valorTotalCorte = Number((costoDirectoCorteTotal + valorAiuTotal).toFixed(2))

      const nuevaActa: ActaCorteObra = {
        id: crypto.randomUUID(),
        consecutivo,
        proyecto_id: proyecto.id,
        numero_corte: numeroCorte,
        fecha_corte: fechaCorte,
        fecha_inicio_periodo: opciones.fechaInicioPeriodo || proyecto.fecha_inicio,
        fecha_fin_periodo: opciones.fechaFinPeriodo || fechaCorte,
        residente_nombre: opciones.residenteNombre,
        interventor_nombre: opciones.interventorNombre || 'Por designar',
        cliente_nombre: proyecto.cliente || 'CLIENTE GENERAL',
        estado: 'borrador',
        valor_costo_directo: Number(costoDirectoCorteTotal.toFixed(2)),
        valor_aiu: valorAiuTotal,
        valor_total: valorTotalCorte,
        fecha_radicacion: null,
        fecha_cobro: null,
        dias_credito: 30,
        fecha_vencimiento: null,
        detalles_capitulos: capitulosActa,
        notas: opciones.notas || '',
        activo: true,
        created_at: ahora,
        updated_at: ahora,
      }

      const actualizadas = [nuevaActa, ...actas]
      setActas(actualizadas)
      guardarActasLocal(actualizadas)

      // Guardar en Supabase
      try {
        await sb.from('plan_actas_corte').insert({
          id: nuevaActa.id,
          consecutivo: nuevaActa.consecutivo,
          proyecto_id: nuevaActa.proyecto_id,
          numero_corte: nuevaActa.numero_corte,
          fecha_corte: nuevaActa.fecha_corte,
          fecha_inicio_periodo: nuevaActa.fecha_inicio_periodo,
          fecha_fin_periodo: nuevaActa.fecha_fin_periodo,
          residente_nombre: nuevaActa.residente_nombre,
          interventor_nombre: nuevaActa.interventor_nombre,
          cliente_nombre: nuevaActa.cliente_nombre,
          estado: nuevaActa.estado,
          valor_costo_directo: nuevaActa.valor_costo_directo,
          valor_aiu: nuevaActa.valor_aiu,
          valor_total: nuevaActa.valor_total,
          dias_credito: nuevaActa.dias_credito,
          detalles_capitulos: nuevaActa.detalles_capitulos,
          notas: nuevaActa.notas,
          activo: true,
        })
      } catch {}

      return nuevaActa
    },
    [actas]
  )

  // 3. Transición de Estado y Recaudo de Caja (Tarea 2.8)
  const actualizarEstadoActa = useCallback(
    async (
      actaId: string,
      nuevoEstado: EstadoActaCorte,
      datosAdicionales?: {
        fechaRadicacion?: string
        fechaCobro?: string
        diasCredito?: number
      }
    ) => {
      setActas((prev) => {
        const actualizadas = prev.map((a) => {
          if (a.id !== actaId) return a

          const fechaRad = datosAdicionales?.fechaRadicacion || a.fecha_radicacion
          const dias = datosAdicionales?.diasCredito ?? a.dias_credito
          let fechaVenc = a.fecha_vencimiento

          if (fechaRad) {
            const radDate = new Date(fechaRad)
            radDate.setDate(radDate.getDate() + dias)
            fechaVenc = radDate.toISOString().split('T')[0]
          }

          return {
            ...a,
            estado: nuevoEstado,
            fecha_radicacion: fechaRad,
            fecha_cobro: datosAdicionales?.fechaCobro ?? a.fecha_cobro,
            dias_credito: dias,
            fecha_vencimiento: fechaVenc,
            updated_at: new Date().toISOString(),
          }
        })

        guardarActasLocal(actualizadas)

        const match = actualizadas.find((a) => a.id === actaId)
        if (match) {
          void sb
            .from('plan_actas_corte')
            .update({
              estado: match.estado,
              fecha_radicacion: match.fecha_radicacion,
              fecha_cobro: match.fecha_cobro,
              dias_credito: match.dias_credito,
              fecha_vencimiento: match.fecha_vencimiento,
              updated_at: match.updated_at,
            })
            .eq('id', actaId)
        }

        return actualizadas
      })
    },
    []
  )

  const eliminarActa = useCallback((actaId: string) => {
    setActas((prev) => {
      const filtradas = prev.filter((a) => a.id !== actaId)
      guardarActasLocal(filtradas)
      void sb.from('plan_actas_corte').delete().eq('id', actaId)
      return filtradas
    })
  }, [])

  return {
    actas: actasProyecto,
    todasLasActas: actas,
    cortes,
    cargando,
    registrarCorteAvance,
    generarActaCorte,
    actualizarEstadoActa,
    eliminarActa,
    refrescar: sincronizar,
  }
}

